/**
 * HomePage - default v2 dashboard view. Live data only.
 *
 * Per CEO directive 2026-04-28: no mock anywhere. Sections that don't have
 * a live source are removed entirely (not replaced with placeholders).
 *
 * Live sections shipped here:
 *   • Live Strip (wired to /api/v1/live/counters)
 *   • Hero tiles (Revenue today, Fans/Creators joined today) — /api/v1/dashboard/home
 *   • 12-month revenue trend
 *   • Top 5 creators by call volume (this month)
 *   • Top 5 fans by spend (this month, masked handles)
 */
import React from "react";
import { tokensV2 as T } from "./tokens.v2";
import { Tile } from "./Tile.v2";
import { AreaChart } from "./AreaChart.v2";
import { Leaderboard } from "./Leaderboard.v2";
import { PageGuide } from "./PageGuide.v2";
import { LiveStrip } from "./LiveStrip.v2";
import { useHomeData, formatINR, formatNum } from "./useHomeData.v2";
import { rangeWordFor, granLabel, granWord } from "./rangeWord.v2";
import { ForecastSection } from "./ForecastSection.v2";
import { CohortRetentionCard } from "./InsightsCards.v2";
import { friendlyError } from "./errorMessage.v2";
import { EmptyError, LoadingShell } from "./PageState.v2";

function deltaForTile(apiDelta, prev) {
  if (!apiDelta || apiDelta.value == null || typeof apiDelta.value !== "number") return null;
  return {
    direction: apiDelta.direction || "flat",
    value: apiDelta.value.toFixed(1) + "%",
    unit: prev?.label || undefined,
  };
}

function ErrorBanner({ message, onRetry }) {
  return (
    <div style={{
      marginBottom: 12, padding: "10px 14px",
      background: T.status.warnBg, border: `1px solid ${T.status.warn}`,
      borderRadius: 8, fontSize: 12, color: T.text.secondary,
      display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
    }}>
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: T.status.warn, padding: "3px 8px", borderRadius: 999, background: "#FFFFFF" }}>Heads up</span>
      <span style={{ flex: 1 }}>{friendlyError(message)}</span>
      {onRetry && (
        <button type="button" onClick={onRetry} style={{
          background: T.surface.card, border: `1px solid ${T.border.subtle}`,
          borderRadius: 8, padding: "5px 12px",
          fontSize: 12, fontWeight: 700, color: T.text.primary, cursor: "pointer",
        }}>Retry</button>
      )}
    </div>
  );
}

function LoadingTile() {
  return (
    <div style={{
      background: T.surface.card, border: `1px solid ${T.border.subtle}`,
      borderRadius: 16, padding: 18, height: 220,
      display: "flex", alignItems: "center", justifyContent: "center",
      color: T.text.muted, fontSize: 13,
    }}>
      Loading…
    </div>
  );
}

export function HomePage({ onSelectSection, onOpenMetric, range, rangeId }) {
  const { data, loading, error, reload } = useHomeData(range, rangeId);
  const word = rangeWordFor(rangeId);

  const buildHero = () => {
    if (!data?.hero) return [];
    const r = data.hero.revenueToday;
    const f = data.hero.fansJoinedToday;
    const c = data.hero.creatorsJoinedToday;
    const tiles = [];

    if (r) {
      tiles.push({
        metricId: "revenue-today",
        label: `Call revenue · ${word}`,
        value: typeof r.value === "number" ? formatINR(r.value) : "—",
        subValue: r.prev?.value != null ? `vs ${formatINR(r.prev.value)} prev period` : null,
        delta: deltaForTile(r.delta, r.prev),
        icon: "R", variant: "muted-orange", targetPage: "money",
      });
    }
    if (f) {
      // Sub-line shows the previous-period value alongside the lifetime total
      // (e.g. "vs 4 prev · of 1,01,560 total") so the comparison is concrete,
      // not just a percentage.
      const fanSubParts = [];
      if (f.prev?.value != null) fanSubParts.push(`vs ${formatNum(f.prev.value)} prev`);
      if (f.totalFans) fanSubParts.push(`of ${formatNum(f.totalFans)} total`);
      tiles.push({
        metricId: "fans-joined-today",
        label: `Fans joined · ${word}`,
        value: typeof f.value === "number" ? formatNum(f.value) : "—",
        subValue: fanSubParts.length > 0 ? fanSubParts.join(" · ") : null,
        delta: deltaForTile(f.delta, f.prev),
        icon: "F", variant: "muted-violet", targetPage: "fans",
      });
    }
    if (c) {
      const creatorSubParts = [];
      if (c.prev?.value != null) creatorSubParts.push(`vs ${formatNum(c.prev.value)} prev`);
      if (c.totalCreators) creatorSubParts.push(`of ${formatNum(c.totalCreators)} total`);
      tiles.push({
        metricId: "creators-joined-today",
        label: `Creators joined · ${word}`,
        value: typeof c.value === "number" ? formatNum(c.value) : "—",
        subValue: creatorSubParts.length > 0 ? creatorSubParts.join(" · ") : null,
        delta: deltaForTile(c.delta, c.prev),
        icon: "C", variant: "muted-lime", targetPage: "creators",
      });
    }
    return tiles;
  };

  const heroTiles = buildHero();
  const trendPoints = data?.trend?.revenueLast12Months || [];
  const topCreators = (data?.leaderboards?.topCreators || []).map(r => ({
    name: r.name,
    value: r.value,
    displayValue: formatINR(r.value),
    // Real subtitle: how many earning entries (= completed paid calls) drove this number
    subtitle: r.callCount > 0
      ? `${formatNum(r.callCount)} earning ${r.callCount === 1 ? 'entry' : 'entries'} · ${word}`
      : null,
  }));
  const topFans = (data?.leaderboards?.topFans || []).map(r => ({
    name: r.name,
    value: r.value,
    displayValue: formatINR(r.value),
    // Real subtitle: how many transactions drove this fan's total
    subtitle: r.transactionCount > 0
      ? `${formatNum(r.transactionCount)} ${r.transactionCount === 1 ? 'payment' : 'payments'} · ${word}`
      : null,
  }));

  const latestRevenue = trendPoints[trendPoints.length - 1]?.value || 0;
  const prevRevenue   = trendPoints[trendPoints.length - 2]?.value || 0;
  const summaryDelta  = prevRevenue > 0 ? ((latestRevenue - prevRevenue) / prevRevenue) * 100 : 0;

  // While loading — pure skeleton, no other UI competes during a fetch.
  if (loading) {
    return <LoadingShell tiles={3} />;
  }
  // Initial-load failure: full-page friendly empty state, not a persistent
  // banner over the rest of the page.
  if (!data && error) {
    return <EmptyError title="Couldn't load Home" error={error} onRetry={reload} />;
  }

  return (
    <>
      <LiveStrip />

      {/* Soft warning ONLY if we have stale data AND a fresh error came in
          (e.g. retry mid-session failed). Hidden during normal load. */}
      {error && data && <ErrorBanner message={error} onRetry={reload} />}

      {/* Hero tiles — render real values; LoadingShell handles the loading state above */}
      {false && (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(240px, 100%), 1fr))",
          gap: 14, marginBottom: 12,
        }}>
          <LoadingTile /><LoadingTile /><LoadingTile />
        </div>
      )}

      {heroTiles.length > 0 && (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(240px, 100%), 1fr))",
          gap: 14, marginBottom: 12,
        }}>
          {heroTiles.map(t => (
            <Tile key={t.label} {...t} onClick={() => {
              if (onOpenMetric && t.metricId) {
                onOpenMetric(t.metricId, {
                  value: t.value, subValue: t.subValue, label: t.label, delta: t.delta,
                });
              } else if (onSelectSection && t.targetPage) {
                onSelectSection(t.targetPage);
              }
            }} />
          ))}
        </div>
      )}

      {/* Forecast section — predictions for end-of-today / week / month
          revenue + projected top creators/fans/brands. Confidence-badged. */}
      <ForecastSection />

      {/* Cohort retention — proves whether new signups are converting to
          paying within 30/60/90 days, broken down by signup month. */}
      <div style={{ marginBottom: 14 }}>
        <CohortRetentionCard />
      </div>

      {/* Revenue trend — title and granularity badge adapt to the date pill */}
      {trendPoints.length > 0 && (() => {
        const gran  = data?.asOf?.granularity || 'month';
        const isLifetime = rangeId === 'till-date';
        const titleSuffix = isLifetime ? 'last 12 months' : word;
        return (
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10, padding: "0 4px" }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text.primary }}>Call revenue trend — {titleSuffix}</div>
                <div style={{ fontSize: 11, color: T.text.muted, marginTop: 2 }}>live · {granWord(gran)} fan call/room revenue</div>
              </div>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.text.muted, padding: "4px 10px", background: T.surface.card, border: "1px solid " + T.border.subtle, borderRadius: 999 }}>{granLabel(gran)}</span>
            </div>
            <AreaChart
              data={trendPoints}
              format={formatINR}
              summaryLabel={trendPoints[trendPoints.length - 1]?.label || "Latest"}
              summaryValue={formatINR(latestRevenue)}
              summaryDelta={(summaryDelta >= 0 ? "+" : "") + summaryDelta.toFixed(1) + "% vs prev"}
              prevLabel={data?.range?.prevLabel ? data.range.prevLabel.replace(/^vs\s+/, "") : "Previous"}
            />
          </div>
        );
      })()}

      {/* Top creators / top fans leaderboards — live, real names for creators, masked for fans */}
      {(topCreators.length > 0 || topFans.length > 0) && (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(320px, 100%), 1fr))",
          gap: 14, marginBottom: 14,
        }}>
          {topCreators.length > 0 && (
            <Leaderboard
              title="Top creators"
              subtitle={`By net earnings — ${word} · sourced from earnings table (excl. TDS)`}
              rows={topCreators}
              rightLabel="Earnings"
            />
          )}
          {topFans.length > 0 && (
            <Leaderboard
              title="Top fans"
              subtitle={`By spend on creators — ${word} · CEO-only view (real names)`}
              rows={topFans}
              rightLabel="Spend"
            />
          )}
        </div>
      )}
      <PageGuide groups={HOME_GUIDE} />
    </>
  );
}

// Definitions for every Home-page card. Money excludes test accounts + NaN.
const HOME_GUIDE = [
  {
    section: "Live strip (today)",
    rows: [
      { card: "Total inflow", means: "All money in today — call revenue (fan calls/rooms) PLUS brand wallet deposits. Hover to see the split.", rule: "transaction_history (paid call/room, not refund, not top-up) + brand_transaction_history (wallet_deposit, credited). NaN-guarded." },
      { card: "Wallet top-ups (real)", means: "Real cash fans added to their wallet today via UPI / card (not yet spent). Fan wallets only.", rule: "transaction_history is_add_credit=true, is_paid=success, celebrity_id IS NULL, real payment method (not admin/referral credit), today." },
      { card: "Fancall credit", means: "Credit Fancall GAVE fan wallets today — admin grants + referral/reward credits. Kept separate from real cash so inflow isn't overstated.", rule: "transaction_history is_add_credit=true, is_paid=success, celebrity_id IS NULL, payment_method='admin-manual' OR referral/reward/admin credit type, today." },
      { card: "Total signups", means: "Fans + creators + brands who COMPLETED onboarding today.", rule: "fans phone_verified + creators (is_active OR is_verify) + brands is_verified, created today." },
      { card: "Calls completed", means: "Direct calls that connected and finished today.", rule: "celebrity_slot_booking status='completed', today." },
      { card: "Referral fans", means: "New fans today who joined via a fan or creator referral.", rule: "user_referral where the joiner is a fan, today (excludes synthetic account)." },
      { card: "Pending payouts", means: "Creator + brand withdrawal requests awaiting processing (live).", rule: "withdraw_requests + brand_withdraw_requests, status='pending' — live snapshot." },
      { card: "Refunds today / Tickets open", means: "Refunds requested today; open (unblocked) support reports.", rule: "refund_requests today; report where not blocked." },
      { card: "Calls in progress", means: "Live calls happening right now.", rule: "Agora active channels (60-second cache)." },
    ],
  },
  {
    section: "Headline tiles",
    rows: [
      { card: "Call revenue · window", means: "Money earned from fan calls/rooms in the period (incl. 18% GST), vs the previous period. Does NOT include brand wallet deposits.", rule: "transaction_history: is_paid=success, not refund, not top-up, celebrity_id set, type IN (call-booking, room-booking). NaN-guarded, test accounts excluded." },
      { card: "Fans joined · window", means: "Fans who completed signup (phone-verified) in the period; also shows cumulative total + change.", rule: "users role=fan, phone_verified=true, created in window." },
      { card: "Creators joined · window", means: "Creators who completed signup (active or accepted) in the period.", rule: "users role=creator, is_active OR is_verify, created in window." },
    ],
  },
  {
    section: "Trend & leaderboards",
    rows: [
      { card: "Call revenue trend", means: "Call revenue over time (auto hour/day/month/year) with the previous period overlaid.", rule: "Same filter as the Call revenue tile (fan calls/rooms only), bucketed by period." },
      { card: "Top creators", means: "Top 5 creators by net earnings in the period (calls + brand + affiliate + referral, minus TDS).", rule: "transaction_history type='youtuber-earning' (net call income) + campaign earnings + referral rewards. NOT the earnings table (it over-counts)." },
      { card: "Top fans", means: "Top 5 fans by money spent on calls in the period (real names — CEO-only).", rule: "transaction_history (paid call/room) summed per fan." },
    ],
  },
  {
    section: "Forecast & retention",
    rows: [
      { card: "Forecast", means: "Projection of where key metrics are heading.", rule: "Separate forecast model with its own data source." },
      { card: "Cohort retention", means: "Of fans who joined in a month, how many returned in later months.", rule: "Completed (phone-verified) fans grouped by signup month; return = activity in a later month." },
    ],
  },
];
